import React from "react";
import { Box, Divider, Image, Stack, Text } from "@mantine/core";
import { BusinessInfo } from "../../types/context";
import {
  computeTransactionTotals,
  visibleRefunds,
} from "../../utils/transactionTotals";
import {
  appointmentStart,
  clientName,
  ReceiptTransaction,
  receiptLineItems,
} from "../../utils/receiptModel";
import { paymentStatusTag } from "../../utils/paymentStatusTag";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { formatDateTime, DEFAULT_TIMEZONE } from "../../utils/timeUtils";
import { formatPaymentMethod } from "../../utils/paymentMethodFormatters";
import styles from "./TransactionReceipt.module.css";

/** Thin rule between receipt sections (near-black-friendly grey for print). */
const SectionRule = () => <Divider my={12} color="#999" />;

export interface TransactionReceiptProps {
  transaction: ReceiptTransaction;
  businessInfo: BusinessInfo | null;
}

/**
 * Order-level receipt shared by the Sales detail drawers and the calendar
 * appointment drawer / mobile sheet. Rendered print-only (hidden on screen; the
 * `@media print` block in the CSS module makes it the sole visible element) —
 * print it through `useReceiptPrint`, never by mounting it directly.
 * Calculation rows come straight from `computeTransactionTotals` — taxes are
 * read from what is persisted on the transaction, never recomputed. Money is
 * always formatted via `formatCurrency`.
 */
export default function TransactionReceipt({
  transaction,
  businessInfo,
}: TransactionReceiptProps) {
  const totals = computeTransactionTotals(transaction);
  const lineItems = receiptLineItems(transaction);
  const refunds = visibleRefunds(transaction);

  // Only real, succeeded cash-bearing tenders (mirror the summary's filter).
  const succeededPayments = transaction.payments.filter(
    (p) => p.status === "succeeded" && p.type !== "package_redemption",
  );

  const {
    reference_id: orderNumber,
    created_dt: createdDt,
    appointment_status: appointmentStatus,
    transaction_status: transactionStatus,
    refund_status: refundStatus,
  } = transaction.transaction;
  const apptStart = appointmentStart(transaction);
  // Receipt datetimes render in the business location's timezone (not the
  // viewer's browser), matching the Sales tables/drawers.
  const timezone = businessInfo?.timezone || DEFAULT_TIMEZONE;
  const businessName = businessInfo?.salon_name || "Receipt";
  const logoUrl = businessInfo?.logo_url || null;

  const money = (amount: number) => formatCurrency(amount);

  const receiptRow = (label: string, value: string, key?: React.Key) => (
    <Box key={key} className={styles.row}>
      <Text component="span" className={styles.rowLabel} size="sm">
        {label}
      </Text>
      <Text component="span" className={styles.rowValue} size="sm">
        {value}
      </Text>
    </Box>
  );

  return (
    <Box
      className={styles.printOnly}
      aria-hidden
      data-testid="transaction-receipt"
    >
      <Box className={styles.receipt}>
        <Stack gap={2} align="center" ta="center">
          {logoUrl && (
            <Image
              src={logoUrl}
              alt={businessName}
              fit="contain"
              mah={56}
              w="auto"
              mb={6}
              className={styles.logo}
            />
          )}
          <Text className={styles.businessName}>{businessName}</Text>
          {orderNumber && (
            <Text className={styles.meta} size="sm">
              Order #{orderNumber}
            </Text>
          )}
          {createdDt && (
            <Text className={styles.meta} size="sm">
              {formatDateTime(createdDt, timezone, "MMM D, YYYY h:mm A")}
            </Text>
          )}
        </Stack>

        <SectionRule />

        {/* Who / when / where it stands */}
        <Stack gap={4}>
          {receiptRow("Client", clientName(transaction))}
          {apptStart &&
            receiptRow(
              "Appointment",
              formatDateTime(apptStart, timezone, "ddd, MMM D, YYYY · h:mm A"),
            )}
          {appointmentStatus &&
            receiptRow("Appointment status", appointmentStatus)}
          {receiptRow(
            "Payment status",
            paymentStatusTag(transactionStatus, refundStatus).label,
          )}
        </Stack>

        <SectionRule />

        {/* Itemized line items: services, products, packages, gift cards */}
        <Stack gap={6}>
          {lineItems.map((item) =>
            receiptRow(item.label, money(item.amount), item.key),
          )}
        </Stack>

        <SectionRule />

        {/* Calculations — straight from computeTransactionTotals. */}
        <Stack gap={4}>
          {receiptRow("Subtotal", money(totals.subtotalAfterRedemptions))}
          {totals.discountSavings > 0 &&
            receiptRow("Discount", `−${money(totals.discountSavings)}`)}
          {totals.taxBreakdown.map((tax) =>
            receiptRow(
              `${tax.name} (${(tax.rate * 100).toFixed(3)}%)`,
              money(tax.amount),
              `tax-${tax.id}`,
            ),
          )}
          {totals.tipsTotal > 0 && receiptRow("Tip", money(totals.tipsTotal))}
        </Stack>

        <SectionRule />

        <Box className={styles.totalRow}>
          <Text component="span" inherit>
            Total
          </Text>
          <Text component="span" inherit>
            {money(totals.totalAfterRedemptions)}
          </Text>
        </Box>

        {/* Tenders */}
        {succeededPayments.length > 0 && (
          <>
            <SectionRule />
            <Text fw={700} tt="uppercase" fz={11} lts="0.04em" mb={4}>
              Payments
            </Text>
            <Stack gap={4}>
              {succeededPayments.map((payment) =>
                receiptRow(
                  `${payment.type} (${formatPaymentMethod(payment.method)})`,
                  money(payment.amount),
                  `pay-${payment.id}`,
                ),
              )}
            </Stack>
          </>
        )}

        {/* Refunds as negatives */}
        {refunds.length > 0 && (
          <>
            <SectionRule />
            <Text fw={700} tt="uppercase" fz={11} lts="0.04em" mb={4}>
              Refunds
            </Text>
            <Stack gap={4}>
              {refunds.map((refund) =>
                receiptRow(
                  `Refunded via ${formatPaymentMethod(refund.method)}${
                    refund.status === "pending" ? " (pending)" : ""
                  }`,
                  `−${money(refund.amount)}`,
                  `ref-${refund.id}`,
                ),
              )}
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}
